import styled, { css } from 'styled-components';

import { ArticleFadeInImage } from 'editorial/components/ArticleFadeInImage';
import { ArticleImage } from 'editorial/components/ArticleImage';
import { IArticleImageSetSection, IArticleSectionAdditionalProps } from 'editorial/types';
import { getOrientation, getOrientationString } from 'editorial/utils/getImageSetOrientation';

export const ArticleImageSet = ({
  pictures,
  caption,
  credits,
}: IArticleImageSetSection & IArticleSectionAdditionalProps) => {
  const isBothPortrait = getOrientationString(pictures) === 'portrait,portrait';
  const isSingleImage = pictures.length < 2;

  return (
    <ArticleImageSet.Wrapper>
      <ArticleImageSet.ImagesWrapper $isSingleImage={isSingleImage}>
        <ArticleImageSet.ImageWrapper
          $orientation={getOrientation(pictures[0])}
          $isSingleImage={isSingleImage}
          $isBothPortrait={isBothPortrait}
          $isFirstImage
        >
          <ArticleFadeInImage triggerDistance={100}>
            <ArticleImage picture={pictures[0]} caption={caption} credits={credits} />
          </ArticleFadeInImage>
        </ArticleImageSet.ImageWrapper>
        {pictures.length === 2 && (
          <ArticleImageSet.ImageWrapper
            $orientation={getOrientation(pictures[1])}
            $isSingleImage={isSingleImage}
            $isBothPortrait={isBothPortrait}
          >
            <ArticleFadeInImage triggerDistance={100} delayAnimation>
              <ArticleImage picture={pictures[1]} caption={caption} credits={credits} />
            </ArticleFadeInImage>
          </ArticleImageSet.ImageWrapper>
        )}
      </ArticleImageSet.ImagesWrapper>
    </ArticleImageSet.Wrapper>
  );
};

ArticleImageSet.Wrapper = styled.section`
  margin: 60px 0;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 10em 0;
    & + & {
      margin: 5em auto 10em;
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    margin: 10em auto;
    max-width: 50%;
  }
`;

ArticleImageSet.ImagesWrapper = styled.div<{ $isSingleImage: boolean }>`
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0;
    align-items: center;
    max-width: 1186px;

    ${({ $isSingleImage }) =>
      $isSingleImage &&
      `
      align-items: center;
      justify-content: center;
  `}
  }
`;

ArticleImageSet.ImageWrapper = styled.div<{
  $isBothPortrait: boolean;
  $isSingleImage: boolean;
  $isFirstImage?: boolean;
  $orientation: string;
}>`
  max-width: none;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  &:nth-child(2) {
    margin-top: 60px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: ${({ $isSingleImage, $orientation, $isBothPortrait }) => {
        if ($isBothPortrait) {
          return '40%';
        }

        if (!$isSingleImage) {
          if ($orientation === 'portrait') {
            return '33%';
          }
          return '50%';
        }
      }}
      ${({ $isSingleImage, $isFirstImage }) =>
        $isSingleImage &&
        $isFirstImage &&
        css`
          max-width: (90px * 5) + (26px * 4);
        `};
  }
`;
